import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import Tooltip from '@components/Tooltip';
import { CHANNEL, STATUS } from '@constants/channels';
import { getChannelIcon, liveChatFollowUpContactField } from '@helpers/channels';
import { useTranslation } from './useTranslation';

export const useChannels = () => {
    const channels = useSelector(state => state.channels.data);
    const { t } = useTranslation();

    const channelsMap = useMemo(
        () =>
            channels.reduce((acc, channel) => {
                acc[channel.id] = channel;
                return acc;
            }, {}),
        [channels],
    );

    const getAccessibleChannelIds = filterCb => {
        const hasFilterCb = typeof filterCb === 'function';
        const filteredChannels = hasFilterCb ? channels.filter(filterCb) : channels;
        return filteredChannels.map(c => c.id);
    };

    const FILTER = {
        in: false,
        out: false,
        status: true,
        contact: null,
        withArchivedMultiselect: false,
        withArchivedSelect: false,
        withDisconnected: false,
        withPluspointEmail: false,
        withDisabledAgents: false,
        currentAgent: null,
    };

    const createChannelLabel = params => (
        <span
            style={{
                display: 'grid',
                alignItems: 'center',
                gridGap: '8px',
                gridTemplateColumns: '16px calc(100% - 24px)',
            }}
        >
            {getChannelIcon(params.type)}{' '}
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{params.name}</span>
        </span>
    );
    /**
     * @param {Object} filter - Object with in and out boolean values for filtering incoming and outgoing channels and status
     *                          for filtering by status SUCCESS and contact for filtering by email and phoneNumber
     * @param {Array} availableChannels - Array of channel ids.
     * @param {Array} channels - Array of channels.
     * @returns {ReactNode} Returns filtered channel option for multiselect.
     */
    const getChannelOptions = (filter, availableChannels) => {
        const updatedFilter = { ...FILTER, ...filter };
        const options = availableChannels
            .filter(channelId => {
                const channel = channelsMap[channelId];
                if (!channel) return false;
                if (channel?.incoming !== updatedFilter.in && channel?.outgoing !== updatedFilter.out) {
                    return false;
                }
                if (
                    updatedFilter.status &&
                    channel?.status !== STATUS.SUCCESS &&
                    (channel?.status !== STATUS.DISCONNECTED || !updatedFilter.withDisconnected)
                ) {
                    return false;
                }
                if (
                    updatedFilter?.contact &&
                    ((channel?.type === CHANNEL.EMAIL && !updatedFilter?.contact?.email) ||
                        ((channel?.type === CHANNEL.SMS ||
                            channel?.type === CHANNEL.WHATSAPP ||
                            channel?.type === CHANNEL.WHATSAPP_BUSINESS) &&
                            !updatedFilter?.contact?.phoneNumber))
                ) {
                    return false;
                }
                if (updatedFilter?.contact && channel?.type === CHANNEL.LIVE_CHAT) {
                    const needed = liveChatFollowUpContactField(channel, channelsMap);
                    if (
                        (needed === 'phone' && !updatedFilter.contact.phoneNumber) ||
                        (needed === 'email' && !updatedFilter.contact.email)
                    ) {
                        return false;
                    }
                }
                return true;
            })
            ?.map(channelId => {
                const channel = channelsMap[channelId];
                const isDisabledByAgent =
                    updatedFilter.withDisabledAgents &&
                    channel?.agentId &&
                    channel?.agentId !== updatedFilter.currentAgent;
                return {
                    value: channel?.id,
                    disabled: isDisabledByAgent,
                    label: isDisabledByAgent ? (
                        <Tooltip disableInteractive title={t('Agents.channelTakenByAgent')} placement="top">
                            {createChannelLabel({ name: channel?.name, type: channel?.type })}
                        </Tooltip>
                    ) : (
                        createChannelLabel({ name: channel?.name, type: channel?.type })
                    ),
                    name: channel?.name,
                    type: channel?.type,
                };
            });

        return [
            ...options,
            ...(updatedFilter.withArchivedMultiselect || updatedFilter.withArchivedSelect
                ? [
                      {
                          value: updatedFilter.withArchivedMultiselect ? CHANNEL.ARCHIVED : 'ARCHIVED',
                          name: t('common.archived'),
                          label: createChannelLabel({
                              name: t('common.archived'),
                              type: CHANNEL.ARCHIVED,
                          }),
                      },
                  ]
                : []),
            ...(updatedFilter.withPluspointEmail
                ? [
                      {
                          value: 'PLUSPOINT_EMAIL',
                          name: 'Pluspoint Email',
                          label: createChannelLabel({
                              name: 'Pluspoint Email',
                              type: CHANNEL.EMAIL,
                          }),
                      },
                  ]
                : []),
        ];
    };

    const checkTwoWayLiveChat = channel =>
        channel.type === CHANNEL.LIVE_CHAT && channel.liveChatParameters?.notifyAboutMissingConversations;

    const canInitiateConversation = channel =>
        (channel.incoming && ![CHANNEL.LIVE_CHAT, CHANNEL.FACEBOOK, CHANNEL.INSTAGRAM].includes(channel.type)) ||
        checkTwoWayLiveChat(channel);

    return {
        channels,
        getChannelOptions,
        getAccessibleChannelIds,
        checkTwoWayLiveChat,
        canInitiateConversation,
    };
};
